<template>
	<main class="auth-callback">
		<div class="loading-container">
			<img class="logo" src="@/assets/images/logo.svg" alt="Landerz logo" />
			<div class="loading-spinner"></div>
			<p>Completing authentication...</p>
			<div v-if="error" class="error-message">
				{{ error }}
			</div>
		</div>
	</main>
</template>

<script lang="ts" setup>
import { ref, onMounted } from "vue"
import { useRouter, useRoute } from "vue-router"
import { UseRootStore } from "@/model/RootStore"
import { validateIDToken } from "@/utils/ApiClient"
import { analytics } from "@/utils/analytics"
import { supabase } from "@/utils/supabaseClient"
import { finishLoginAndRedirect } from "@/utils/sessionSync"
import { parseAuthenticationProvider } from "@/utils/authenticationProvider"
import { CtaSource, parseCtaSource } from "@/utils/ctaSource"
import { useUIStore } from "@/stores/uiStore"

const store = UseRootStore()
const uiStore = useUIStore()
const router = useRouter()
const route = useRoute()

const error = ref("")

onMounted(async () => {
	try {
		const code = route.query.code as string
		const state = route.query.state as string

		if (!code) {
			throw new Error("No authorization code received")
		}

		const storedState = localStorage.getItem("oauth_state")
		if (!state || state !== storedState) {
			throw new Error("Invalid state parameter")
		}
		localStorage.removeItem("oauth_state")

		// Detect which OAuth provider was used
		const storedProvider = localStorage.getItem("oauth_provider")
		localStorage.removeItem("oauth_provider")
		const provider = storedProvider === null ? "google" : parseAuthenticationProvider(storedProvider)

		if (!provider) {
			throw new Error("Invalid authentication provider")
		}

		const source = parseCtaSource(localStorage.getItem("oauth_source")) ?? CtaSource.LoginPage
		localStorage.removeItem("oauth_source")

		// Determine the backend endpoint and Supabase provider name based on the OAuth provider
		const oauthEndpoint = provider === "outlook" ? "oauth/outlook" : "oauth/google"
		const supabaseProvider = provider === "outlook" ? "azure" : "google"

		// Check the code by calling our endpoint at /oauth/google or /oauth/outlook
		const tokenResponse = await fetch(`${import.meta.env.VITE_AUTH_API_ENDPOINT}${oauthEndpoint}`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				code: code,
				redirect_uri: `${window.location.origin}/auth/callback`,
			}),
		})

		if (!tokenResponse.ok) {
			throw new Error("Failed to exchange code for tokens")
		}

		const tokens = await tokenResponse.json()

		if (!tokens.id_token) {
			throw new Error("No ID token received")
		}

		// Sign in to Supabase with the ID token (use "azure" for Outlook/Microsoft, "google" for Google)
		const { data, error: supabaseError } = await supabase.auth.signInWithIdToken({
			provider: supabaseProvider,
			token: tokens.id_token,
		})

		if (supabaseError) {
			throw new Error(supabaseError.message)
		}

		if (!data.session?.access_token) {
			throw new Error("No session created")
		}

		// Validate the token with your backend
		const validationResult = await validateIDToken(data.session.access_token)

		if (!validationResult.success) {
			throw new Error(validationResult.error || "Backend validation failed")
		}

		// Save the token and redirect
		store.userApiToken = validationResult.token
		store.identifyCurrentUser()
		analytics.userSignUpDateIdentified(validationResult.user?.createdAt)

		if (validationResult.user?.isNewUser) {
			analytics.userSignedUp({ provider, source })
			uiStore.requestExtendedAccessWelcome()
		}
		analytics.userSignedIn({ provider, source })

		// Check if there's a redirect parameter in localStorage or in the search query. We store it in the LoginPage
		const redirectPath = localStorage.getItem("oauth_redirect") || (route.query.redirect as string | undefined)
		if (localStorage.getItem("oauth_redirect")) {
			localStorage.removeItem("oauth_redirect")
		}
		const result = await finishLoginAndRedirect(validationResult.token, redirectPath, path => router.push(path))
		if (!result.ok) {
			throw new Error(result.error)
		}
	} catch (err) {
		console.error("OAuth callback error:", err)
		error.value = err instanceof Error ? err.message : "Authentication failed"

		// Redirect to login page after a delay
		setTimeout(() => {
			router.push("/login")
		}, 3000)
	}
})
</script>

<style scoped>
.auth-callback {
	display: flex;
	justify-content: center;
	align-items: center;
	min-height: 100vh;
	background: var(--neutral-0);
}

.loading-container {
	text-align: center;
	padding: 2rem;
}

.logo {
	width: 120px;
	height: auto;
	margin-bottom: 2rem;
}

.loading-spinner {
	width: 40px;
	height: 40px;
	border: 4px solid var(--neutral-200);
	border-top: 4px solid var(--primary-green);
	border-radius: 50%;
	animation: spin 1s linear infinite;
	margin: 0 auto 1rem;
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

.error-message {
	color: var(--neutral-0);
	margin-top: 1rem;
	padding: 1rem;
	background: var(--accent-warning);
	border-radius: var(--border-radius-md);
}
</style>
